import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AdminReadingChatResponseDto, ArcanaReadingQueueItemDto, ArcanaReadingQueueResponseDto } from '@lunary/api-client'
import { api } from '../../shared/api/client'
import { ARCANA_READINGS_KEY } from './labels'
import type { QueueFilter } from './labels'

const path = (id: string) => `/admin/arcana-readings/${encodeURIComponent(id)}`

export function useQueue(filter: QueueFilter, enabled: boolean) {
  return useQuery({
    queryKey: [...ARCANA_READINGS_KEY, 'queue', filter],
    queryFn: () => api.get<ArcanaReadingQueueResponseDto>('/admin/arcana-readings', { query: { filter, limit: 100 } }),
    enabled,
    retry: false,
    refetchInterval: 30_000,
  })
}

export function useAdminChat(id: string) {
  return useQuery({
    queryKey: [...ARCANA_READINGS_KEY, 'chat', id],
    queryFn: () => api.get<AdminReadingChatResponseDto>(path(id)),
    retry: false,
    refetchInterval: 30_000,
  })
}

function useAction<T>(fn: (input: T) => Promise<ArcanaReadingQueueItemDto>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ARCANA_READINGS_KEY }),
  })
}

export function useReadingActions(id: string) {
  return {
    start: useAction(() => api.post<ArcanaReadingQueueItemDto>(`${path(id)}/start`)),
    text: useAction((input: { body: string; cards: number[] }) => api.post<ArcanaReadingQueueItemDto>(`${path(id)}/messages`, input)),
    voice: useAction((input: { blob: Blob; caption: string; cards: number[] }) =>
      api.upload<ArcanaReadingQueueItemDto>(`${path(id)}/voice`, input.blob, {
        query: {
          ...(input.caption ? { caption: input.caption } : {}),
          ...(input.cards.length > 0 ? { cards: input.cards.join(',') } : {}),
        },
      }),
    ),
    reject: useAction(() => api.put<ArcanaReadingQueueItemDto>(path(id), { status: 'rejected' })),
    close: useAction(() => api.post<ArcanaReadingQueueItemDto>(`${path(id)}/close`)),
    offerUpgrade: useAction(() => api.post<ArcanaReadingQueueItemDto>(`${path(id)}/upgrade-offer`)),
    cancelUpgrade: useAction(() => api.delete<ArcanaReadingQueueItemDto>(`${path(id)}/upgrade-offer`)),
  }
}

export function useReactToMessage(id: string) {
  const qc = useQueryClient()
  const key = [...ARCANA_READINGS_KEY, 'chat', id]
  return useMutation({
    mutationFn: async (input: { messageId: string; reaction: number | null }): Promise<void> => {
      const url = `${path(id)}/messages/${encodeURIComponent(input.messageId)}/reaction`
      if (input.reaction === null) await api.delete<void>(url)
      else await api.put<void>(url, { reaction: input.reaction })
    },
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: key })
      const previous = qc.getQueryData<AdminReadingChatResponseDto>(key)
      qc.setQueryData<AdminReadingChatResponseDto>(key, (prev) =>
        prev ? { ...prev, messages: prev.messages.map((m) => (m.id === input.messageId ? { ...m, expert_reaction: input.reaction } : m)) } : prev,
      )
      return { previous }
    },
    onError: (_error, _input, context) => {
      qc.setQueryData(key, context?.previous)
    },
  })
}
